import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import type { MouseEventHandler, ReactElement } from "react";
import { ExternalMark, externalLinkProps } from "../external-mark";
import { SiteNavigationDoor } from "./site-navigation-door";
import type {
  SiteNavigationGroup as Group,
  SiteNavigationLink,
  SiteNavigationLinkProps,
} from "./site-navigation-types";

export function SiteNavigationGroup({
  menu,
  renderLink,
  onNavigate,
}: {
  readonly menu: Group;
  readonly renderLink: (props: SiteNavigationLinkProps) => ReactElement;
  readonly onNavigate: MouseEventHandler<HTMLAnchorElement>;
}) {
  const renderDrawerItem = (item: SiteNavigationLink) => (
    <span key={item.href}>
      {renderLink({
        href: item.href,
        ...(item.external ? externalLinkProps : {}),
        onClick: onNavigate,
        ...stylex.props(styles.drawerItem),
        children: (
          <>
            {item.label}
            {item.note && (
              <span {...stylex.props(styles.note)}>{item.note}</span>
            )}
            {item.external && <ExternalMark />}
          </>
        ),
      })}
    </span>
  );
  return menu.href && menu.items.length === 0 && !menu.renderAdditionalItems ? (
    <div {...stylex.props(styles.divided)}>
      {
        <SiteNavigationDoor
          item={{ label: menu.label, href: menu.href }}
          mobile
          renderLink={renderLink}
          onNavigate={onNavigate}
        />
      }
    </div>
  ) : (
    <details {...stylex.props(styles.divided, stylex.defaultMarker())}>
      <summary {...stylex.props(styles.drawerRow)}>
        {menu.label}
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          {...stylex.props(styles.drawerChevron)}
        >
          <path d="m6 8 4 4 4-4" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </summary>
      <div {...stylex.props(styles.drawerItems)}>
        {menu.items.map(renderDrawerItem)}
        {menu.renderAdditionalItems?.(renderDrawerItem)}
      </div>
    </details>
  );
}

const styles = stylex.create({
  divided: {
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  drawerChevron: {
    transform: {
      default: "rotate(0deg)",
      [stylex.when.ancestor("[open]")]: "rotate(180deg)",
    },
  },
  drawerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBlock: 18,
    paddingLeft: spacing.space12,
    paddingRight: spacing.space12,
    width: "100%",
    boxSizing: "border-box",
    color: colors.foreground,
    fontFamily: fonts.mono,
    fontSize: 20,
    lineHeight: "26px",
    letterSpacing: "0.01em",
    cursor: "pointer",
    textDecoration: "none",
    listStyle: "none",
  },
  drawerItems: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    paddingBottom: spacing.space16,
  },
  drawerItem: {
    display: "block",
    borderRadius: radii.small,
    paddingInline: spacing.space12,
    paddingBlock: spacing.space8,
    fontFamily: fonts.mono,
    fontSize: 14,
    lineHeight: "18px",
    color: colors.foreground,
    textDecoration: "none",
    backgroundColor: { default: "transparent", ":hover": colors.hover },
  },
  note: {
    marginLeft: spacing.space4,
    fontFamily: fonts.sans,
    color: colors.muted,
    fontSize: 12,
  },
});
